import { environment } from '@environment';
import { randomByTimestamp } from '@shared/core/utils';
import { bufferDelay } from '../../../rxjs/buffer-delay';
import {
  ITrackingProvider,
  IVimCoreTrackingUserData,
  VimCoreTrackingData,
  VimCoreTrackingProviderBaseConfig,
} from '../interface';
import { Subject, fromEvent, of, Observable, NEVER } from 'rxjs';
import { NgZone, Injectable, Type } from '@angular/core';
import { filter, mergeMap, takeUntil } from 'rxjs/operators';

interface SkyengTrackerConfig {
  source: string;
  token: string;
  tabId: number;
}

type Unixtime = string;

export interface SkyengEventUserData extends IVimCoreTrackingUserData {
  id: number;
}

export enum EDeviceEnvironment {
  browser = 'browser',
  mobile = 'mobile',
}

export interface SkyengEventContext extends VimCoreTrackingData {
  browser_name?: string;
  browser_version?: string;
  device_type: string;
  environment: EDeviceEnvironment;
  mobile: boolean;
  os_name?: string;
  os_version?: string;
  pageUrl: string;
  pagePath: string;
  pageTitle: string;
  referrer: string;
  userAgent: string;
  tabId: number;
}

export interface SkyengEvent {
  source: string;
  type: string;
  timestamp: Unixtime;
  data: Object;
  user: SkyengEventUserData | {};
  context: SkyengEventContext;
}

export interface SkyengTrackingProviderInitConfig {
  key: string;
}

export interface SkyengTrackingProviderConfig
  extends VimCoreTrackingProviderBaseConfig,
    SkyengTrackingProviderInitConfig {
  useClass: Type<SkyengTrackingProvider>;
}

declare global {
  interface Window {
    SKYENG_PROVIDER_TESTS: boolean;
  }
}

export const BUFFER_TIMEOUT_MS = 5 * 1000;
export const TRACK_PAGE_EVENT = 'page';

@Injectable({ providedIn: 'root' })
export class SkyengTrackingProvider implements ITrackingProvider<SkyengTrackingProviderInitConfig> {
  private endpoint = `https://event-analytics-backend.${environment.baseHost}/api/frontend/events`;
  private eventsQueue = new Subject<SkyengEvent>();
  private config: SkyengTrackerConfig;
  private user: SkyengEventUserData | {} = {};

  constructor(private ngZone: NgZone) {
    this.ngZone.runOutsideAngular(() => this.initEventsBuffer());
  }

  init(config: SkyengTrackingProviderInitConfig): void {
    this.config = {
      source: '',
      token: config.key,
      tabId: randomByTimestamp(),
    };
  }

  setUserData(userId: number, user: IVimCoreTrackingUserData = {}): void {
    this.user = { ...user, id: userId };
  }

  track(event: string, data: VimCoreTrackingData): void {
    this.addEventToQueue(event, data);
  }

  page(pageId: string, pageContext: VimCoreTrackingData): void {
    this.addEventToQueue(TRACK_PAGE_EVENT, { ...pageContext, pageId });
  }

  resetUser(): void {
    this.user = {};
  }

  setSource(source: string): void {
    this.config = { ...this.config, source };
  }

  private addEventToQueue(type: string, data: VimCoreTrackingData): void {
    if (!this.config) {
      throw new Error('SkyengTrackingProvider: call `init` before tracking');
    }

    const source = this.config.source;
    const timestamp = Math.round(Date.now() / 1000).toString();
    const user = this.user;
    const context = this.getContext();

    this.checkNestedObjects(data);
    this.checkNestedObjects(user);
    this.checkNestedObjects(context);

    this.eventsQueue.next({ source, type, timestamp, data, user, context });
  }

  private initEventsBuffer(): void {
    const pageUnload$ = fromEvent(window, 'beforeunload');

    this.eventsQueue
      .pipe(
        // eslint-disable-next-line rxjs/no-unsafe-takeuntil
        takeUntil(pageUnload$),
        bufferDelay(BUFFER_TIMEOUT_MS),
        filter(events => events.length > 0),
        mergeMap(events => this.sendEvent(events)),
      )
      .subscribe();
  }

  private sendEvent(events: SkyengEvent[]): Observable<void> {
    if (!this.config.token) {
      return NEVER;
    }

    const url = `${this.endpoint}?token=${this.config.token}`;
    const blobOptions = {
      type: 'text/plain',
    };
    // to check sent data in tests we have to read `Blob` via `FileReader`
    // but we cant do it simply with `fakeAsync`, so temporary uglyhack just for tests
    // https://github.com/angular/angular/issues/31702
    const data = window.SKYENG_PROVIDER_TESTS
      ? // uglyhack for tests, cant extract data from FileReader
        JSON.stringify(events)
      : // ¯\_(ツ)_/¯ to pass headers
        new Blob([JSON.stringify(events)], blobOptions);

    // events must be sent even if page was closed
    navigator.sendBeacon(url, data);

    return of();
  }

  private checkNestedObjects(data: Object): void {
    const hasNestedObjects = Object.values(data).some(
      value => value !== undefined && value !== null && typeof value === 'object',
    );

    if (hasNestedObjects) {
      throw new Error(
        'SkyengTrackingProvider: event data cannot contain nested objects. Data: ' + JSON.stringify(data),
      );
    }
  }

  private getContext(): SkyengEventContext {
    return {
      device_type: 'desktop',
      environment: EDeviceEnvironment.browser,
      mobile: false,
      pageUrl: window.location.href,
      pagePath: window.location.pathname,
      pageTitle: document.title,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      tabId: this.config.tabId,
    };
  }
}
